import React, { useState, useCallback, useRef, useEffect } from 'react';
import { fehlertext } from '../lib/fehlertext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { assistantAPI } from '../lib/api';
import { X, Loader2, User, FileText, ChevronRight, MessageCircle, Send, History, Plus, Star, Maximize2, Minimize2, Copy, RotateCcw, Pencil, Check, ThumbsUp, ThumbsDown, Mic } from 'lucide-react';
import kiIcon from '../assets/icon_ki_helper.webp';
import './AssistantWidget.css';

function StatusBadge({ status }) {
  const map = {
    offen: 'bg-amber-100 text-amber-800',
    abgelehnt: 'bg-red-100 text-red-800',
    eingereicht: 'bg-blue-100 text-blue-800',
    genehmigt: 'bg-green-100 text-green-800',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full ${map[status] || 'bg-slate-100 text-slate-700'}`}>
      {status}
    </span>
  );
}

function EmployeeHit({ item, onClick }) {
  return (
    <button onClick={onClick}
      className="w-full text-left px-3 py-2 rounded-lg bg-slate-50 hover:bg-slate-100 flex items-center justify-between gap-2">
      <span className="truncate">
        <span className="text-sm text-slate-800">{item.name}</span>
        <span className="text-xs text-slate-400 ml-1">{item.email}</span>
      </span>
      <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />
    </button>
  );
}

function AssignmentHit({ item, onClick }) {
  return (
    <button onClick={onClick}
      className="w-full text-left px-3 py-2 rounded-lg bg-slate-50 hover:bg-slate-100">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm text-slate-800 truncate">
          {item.child_name || '\u2014'}{item.school_name ? ` \u00B7 ${item.school_name}` : ''}
        </span>
        <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />
      </div>
      <p className="text-xs text-slate-500 truncate">
        {item.aktenzeichen ? `Az: ${item.aktenzeichen}` : 'kein Az'}
        {item.employee_name ? ` \u00B7 ${item.employee_name}` : ''}
        {item.weekly_hours ? ` \u00B7 ${item.weekly_hours} Std/Wo` : ''}
      </p>
    </button>
  );
}

function UnsubmittedRow({ row, onClick }) {
  return (
    <button onClick={onClick}
      className="w-full text-left px-3 py-2 rounded-lg bg-slate-50 hover:bg-slate-100 transition">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-slate-800 truncate">{row.employee_name || '\u2014'}</span>
        <StatusBadge status={row.status} />
      </div>
      <p className="text-xs text-slate-500 truncate">
        {row.child_name}{row.school_name ? ` \u00B7 ${row.school_name}` : ''}
        {row.aktenzeichen ? ` \u00B7 Az: ${row.aktenzeichen}` : ''}
      </p>
    </button>
  );
}

function CardButton({ card, onClick }) {
  return (
    <button onClick={onClick}
      className="group/card w-full flex items-center gap-2 text-left text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-2 hover:border-blue-300 hover:bg-blue-50/40 transition-colors">
      <span className="flex-1 min-w-0">
        <span className="font-medium text-slate-700">{card.title}</span>
        {card.subtitle ? <span className="text-slate-500">{' \u2014 '}{card.subtitle}</span> : null}
      </span>
      <svg className="h-3.5 w-3.5 shrink-0 text-slate-300 group-hover/card:text-blue-500 transition-colors"
        viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M7 5l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

function UserBubble({ text }) {
  return (
    <div className="flex justify-end">
      <div className="bg-blue-100 text-slate-800 rounded-2xl rounded-br-sm px-3 py-2 text-sm max-w-[85%]">
        <p className="whitespace-pre-wrap">{text}</p>
      </div>
    </div>
  );
}

function renderInline(text) {
  var parts = String(text || '').split(/(\*\*[^*]+\*\*)/g);
  return parts.map(function renderPart(p, i) {
    if (p.length > 4 && p.slice(0, 2) === '**' && p.slice(-2) === '**') {
      return <strong key={i}>{p.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

function renderMarkdown(text) {
  var lines = String(text || '').split('\n');
  var blocks = [];
  var i = 0;
  while (i < lines.length) {
    var raw = lines[i];
    var trimmed = raw.replace(/^\s+/, '');
    var hmatch = trimmed.match(/^(#{1,3})\s+(.*)$/);
    if (hmatch) {
      blocks.push({ type: 'h', level: hmatch[1].length, text: hmatch[2] });
      i = i + 1;
      continue;
    }
    if (trimmed === '---' || trimmed === '***' || trimmed === '___') {
      blocks.push({ type: 'hr' });
      i = i + 1;
      continue;
    }
    if (trimmed.slice(0, 2) === '- ' || trimmed.slice(0, 2) === '• ') {
      var bitems = [];
      while (i < lines.length) {
        var bt = lines[i].replace(/^\s+/, '');
        if (bt.slice(0, 2) !== '- ' && bt.slice(0, 2) !== '• ') { break; }
        bitems.push(bt.slice(2));
        i = i + 1;
      }
      blocks.push({ type: 'ul', items: bitems });
      continue;
    }
    if (/^\d+\.\s/.test(trimmed)) {
      var nitems = [];
      while (i < lines.length) {
        var nt = lines[i].replace(/^\s+/, '');
        if (!/^\d+\.\s/.test(nt)) { break; }
        nitems.push(nt.replace(/^\d+\.\s/, ''));
        i = i + 1;
      }
      blocks.push({ type: 'ol', items: nitems });
      continue;
    }
    if (trimmed.slice(0, 2) === '> ' || trimmed === '>') {
      var qitems = [];
      while (i < lines.length) {
        var qt = lines[i].replace(/^\s+/, '');
        if (qt.slice(0, 2) !== '> ' && qt !== '>') { break; }
        qitems.push(qt.replace(/^>\s?/, ''));
        i = i + 1;
      }
      blocks.push({ type: 'quote', items: qitems });
      continue;
    }
    if (trimmed === '') {
      blocks.push({ type: 'space' });
      i = i + 1;
      continue;
    }
    blocks.push({ type: 'p', text: raw });
    i = i + 1;
  }

  return blocks.map(function renderBlock(b, bi) {
    if (b.type === 'ul') {
      return (
        <ul key={bi} className="list-disc pl-5 my-1 space-y-0.5">
          {b.items.map(function renderBLi(it, ii) { return <li key={ii}>{renderInline(it)}</li>; })}
        </ul>
      );
    }
    if (b.type === 'ol') {
      return (
        <ol key={bi} className="list-decimal pl-5 my-1 space-y-0.5">
          {b.items.map(function renderNLi(it, ii) { return <li key={ii}>{renderInline(it)}</li>; })}
        </ol>
      );
    }
    if (b.type === 'quote') {
      return (
        <blockquote key={bi} className="border-l-2 border-slate-300 pl-3 my-1 text-slate-600 italic">
          {b.items.map(function renderQ(it, ii) { return <div key={ii}>{renderInline(it)}</div>; })}
        </blockquote>
      );
    }
    if (b.type === 'h') {
      var hcls = b.level === 1
        ? 'text-sm font-bold text-slate-800 mt-2 mb-1'
        : (b.level === 2
            ? 'text-[13px] font-semibold text-slate-700 mt-2 mb-0.5'
            : 'text-xs font-semibold text-slate-600 mt-1.5 mb-0.5');
      return <div key={bi} className={hcls}>{renderInline(b.text)}</div>;
    }
    if (b.type === 'hr') {
      return <hr key={bi} className="my-2 border-slate-200" />;
    }
    if (b.type === 'space') {
      return <div key={bi} className="h-2" />;
    }
    return <p key={bi} className="my-1 leading-relaxed">{renderInline(b.text)}</p>;
  });
}

function IconAction({ onClick, label, active, tone, children }) {
  var base = 'relative inline-flex items-center justify-center h-7 w-7 rounded-md transition-colors ';
  var col = active
    ? (tone === 'down' ? 'text-red-600 bg-red-50' : 'text-green-600 bg-green-50')
    : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100';
  return (
    <span className="relative inline-flex group">
      <button type="button" onClick={onClick} aria-label={label} className={base + col}>
        {children}
      </button>
      <span className="pointer-events-none absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-slate-800 px-1.5 py-0.5 text-[10px] text-white opacity-0 group-hover:opacity-100 transition-opacity z-10">
        {label}
      </span>
    </span>
  );
}

function AssistantBubble({ text, cards, sources, navigate, conversationId, turnId, initialRating }) {
  var [copied, setCopied] = useState(false);
  var [rating, setRating] = useState(initialRating || null);
  var [showSources, setShowSources] = useState(false);

  function copyText() {
    navigator.clipboard.writeText(text || '').then(function ok() {
      setCopied(true);
      setTimeout(function resetCopied() { setCopied(false); }, 1500);
    }).catch(function noop() {});
    if (conversationId && turnId) {
      assistantAPI.event(conversationId, turnId, 'copy', '').catch(function noop() {});
    }
  }

  function rate(val) {
    var next = rating === val ? null : val;
    setRating(next);
    if (conversationId && turnId) {
      assistantAPI.feedback(conversationId, turnId, next).catch(function noop() {});
    }
  }

  function openCard(card) {
    if (conversationId && turnId) {
      assistantAPI.event(conversationId, turnId, 'click', card.link || card.title || '').catch(function noop() {});
    }
    if (card.link) navigate(card.link);
  }

  return (
    <div>
      <div className="bg-slate-100 text-slate-800 rounded-2xl rounded-bl-sm px-3 py-2 text-sm max-w-[90%]">
        <div className="leading-relaxed">{renderMarkdown(text)}</div>
        {sources && sources.length > 0 && (
          <div className="mt-2">
            <button type="button"
              onClick={function toggleSources() { setShowSources(function flip(v) { return !v; }); }}
              className="inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-slate-600 transition-colors">
              <ChevronRight className={"h-3 w-3 transition-transform " + (showSources ? "rotate-90" : "")} />
              {showSources ? "Quellen ausblenden" : "Quellen anzeigen"}
            </button>
            {showSources && (
              <div className="mt-1.5 rounded-lg border border-slate-200 bg-slate-50/60 divide-y divide-slate-100 overflow-hidden">
                {sources.map(function renderSource(s, si) {
                  var clickable = Boolean(s.link);
                  return (
                    <button key={si} type="button" disabled={!clickable}
                      onClick={function goSource() { if (s.link) { navigate(s.link); } }}
                      className={"w-full flex items-center gap-2 text-left px-2.5 py-1.5 text-[11px] transition-colors " +
                        (clickable ? "hover:bg-blue-50/60 cursor-pointer" : "cursor-default")}>
                      <span className="font-medium text-slate-600 truncate">{s.label}</span>
                      {s.detail ? <span className="text-slate-400 truncate">{s.detail}</span> : null}
                      {clickable ? <ChevronRight className="h-3 w-3 ml-auto shrink-0 text-slate-300" /> : null}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
        {cards && cards.length > 0 && (
          <div className="mt-2 space-y-1">
            {cards.map(function renderCard(c, j) {
              return <CardButton key={j} card={c} onClick={function go() { openCard(c); }} />;
            })}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 mt-1 pl-1">
        <IconAction onClick={copyText} label={copied ? 'Kopiert' : 'Kopieren'}>
          <Copy className="h-3.5 w-3.5" />
        </IconAction>
        <IconAction onClick={function doUp() { rate('up'); }} label="Gute Reaktion" active={rating === 'up'} tone="up">
          <ThumbsUp className="h-3.5 w-3.5" />
        </IconAction>
        <IconAction onClick={function doDown() { rate('down'); }} label="Schlechte Reaktion" active={rating === 'down'} tone="down">
          <ThumbsDown className="h-3.5 w-3.5" />
        </IconAction>
      </div>
    </div>
  );
}

function HistoryItem({ item, onClick }) {
  const dateStr = item.updated_at ? new Date(item.updated_at).toLocaleString('de-DE') : '';
  return (
    <button onClick={onClick}
      className="w-full text-left px-3 py-2 rounded-lg bg-slate-50 hover:bg-slate-100">
      <p className="text-sm text-slate-800 truncate">{item.title || 'Unterhaltung'}</p>
      <p className="text-xs text-slate-400">{dateStr}</p>
    </button>
  );
}

const FAV_COLORS = {
  slate:  'bg-slate-100 border-slate-200 text-slate-700',
  blue:   'bg-blue-100 border-blue-200 text-blue-800',
  cyan:   'bg-cyan-100 border-cyan-200 text-cyan-800',
  green:  'bg-green-100 border-green-200 text-green-800',
  amber:  'bg-amber-100 border-amber-200 text-amber-800',
  violet: 'bg-violet-100 border-violet-200 text-violet-800',
  rose:   'bg-rose-100 border-rose-200 text-rose-800',
};
const FAV_COLOR_KEYS = ['slate', 'blue', 'cyan', 'green', 'amber', 'violet', 'rose'];

function FavoritesTab({ onUse }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [draftColor, setDraftColor] = useState('blue');
  const dragIndex = useRef(null);

  useEffect(function load() {
    assistantAPI.favorites()
      .then(function ok(r) { setItems(r.data.items || []); })
      .catch(function err() {})
      .finally(function done() { setLoading(false); });
  }, []);

  const persist = useCallback(function persist(next) {
    setItems(next);
    assistantAPI.saveFavorites(next).catch(function err() {});
  }, []);

  function addItem() {
    var text = draft.trim();
    if (!text) return;
    persist([...items, { id: 'tmp-' + Date.now(), text: text, color: draftColor }]);
    setDraft(''); setDraftColor('blue'); setAdding(false);
  }
  function removeItem(id) { persist(items.filter(function keep(x) { return x.id !== id; })); }
  function setColor(id, color) {
    persist(items.map(function map(x) { return x.id === id ? { ...x, color: color } : x; }));
  }
  function move(i, dir) {
    var j = i + dir;
    if (j < 0 || j >= items.length) return;
    var next = items.slice();
    var tmp = next[i]; next[i] = next[j]; next[j] = tmp;
    persist(next);
  }
  function onDropAt(i) {
    var from = dragIndex.current; dragIndex.current = null;
    if (from === null || from === i) return;
    var next = items.slice();
    var moved = next.splice(from, 1)[0];
    next.splice(i, 0, moved);
    persist(next);
  }

  if (loading) return <div className="text-sm text-slate-400 px-1 py-4 text-center">Laden ...</div>;

  return (
    <div className="flex flex-col h-full">
      {items.length > 0 && (
        <div className="flex items-center justify-between mb-2 px-1">
          <span className="text-[12px] text-slate-400">
            {editing ? 'Farben & Reihenfolge anpassen' : (items.length + ' Favoriten')}
          </span>
          <button onClick={function toggleEdit() { setEditing(function p(v) { return !v; }); setAdding(false); }}
            data-testid="fav-edit-toggle"
            className="inline-flex items-center gap-1 text-[12px] text-slate-500 hover:text-slate-800">
            {editing ? <Check className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
            {editing ? 'Fertig' : 'Bearbeiten'}
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto space-y-2 pr-1">
        {items.length === 0 && !adding && (
          <p className="text-sm text-slate-400 px-1 py-4 text-center">Noch keine Favoriten.</p>
        )}

        {items.map(function renderItem(it, i) {
          if (!editing) {
            return (
              <button key={it.id} onClick={function use() { onUse(it.text); }}
                data-testid={'fav-use-' + i}
                className={'block w-full text-left rounded-xl border px-3 py-2 text-sm leading-snug hover:opacity-90 transition ' +
                           (FAV_COLORS[it.color] || FAV_COLORS.slate)}>
                {it.text}
              </button>
            );
          }
          return (
            <div key={it.id} draggable
              onDragStart={function onStart() { dragIndex.current = i; }}
              onDragOver={function onOver(e) { e.preventDefault(); }}
              onDrop={function onDrop() { onDropAt(i); }}
              className={'group rounded-xl border px-2.5 py-2 ' + (FAV_COLORS[it.color] || FAV_COLORS.slate)}>
              <div className="flex items-start gap-2">
                <span className="cursor-grab text-slate-400 select-none mt-0.5" title="Ziehen zum Sortieren">&#x2807;</span>
                <span className="flex-1 text-left text-sm leading-snug">{it.text}</span>
                <div className="flex flex-col items-center gap-0.5">
                  <button onClick={function up() { move(i, -1); }} aria-label="Nach oben"
                    data-testid={'fav-up-' + i}
                    className="text-xs leading-none hover:text-slate-900">&#x25B2;</button>
                  <button onClick={function down() { move(i, 1); }} aria-label="Nach unten"
                    data-testid={'fav-down-' + i}
                    className="text-xs leading-none hover:text-slate-900">&#x25BC;</button>
                </div>
                <button onClick={function del() { removeItem(it.id); }} aria-label="Loeschen"
                  data-testid={'fav-delete-' + i}
                  className="text-slate-400 hover:text-rose-600 text-sm">&#x2715;</button>
              </div>
              <div className="flex items-center gap-1.5 mt-1.5 pl-6">
                {FAV_COLOR_KEYS.map(function swatch(c) {
                  return (
                    <button key={c} onClick={function pick() { setColor(it.id, c); }} aria-label={'Farbe ' + c}
                      className={'h-3.5 w-3.5 rounded-full border ' + FAV_COLORS[c].split(' ')[0] +
                                 (it.color === c ? ' ring-2 ring-offset-1 ring-slate-400' : ' border-slate-300')} />
                  );
                })}
              </div>
            </div>
          );
        })}

        {adding && (
          <div className="rounded-xl border border-slate-200 p-2.5 space-y-2" data-testid="fav-add-form">
            <textarea value={draft} onChange={function onChange(e) { setDraft(e.target.value); }}
              placeholder="Prompt-Text, z. B. 'Wer hat diesen Monat nicht abgegeben?'" rows={2}
              data-testid="fav-add-textarea"
              className="w-full text-sm px-2 py-1.5 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-200" />
            <div className="flex items-center gap-1.5">
              {FAV_COLOR_KEYS.map(function swatch(c) {
                return (
                  <button key={c} onClick={function pick() { setDraftColor(c); }} aria-label={'Farbe ' + c}
                    className={'h-4 w-4 rounded-full ' + FAV_COLORS[c].split(' ')[0] +
                               (draftColor === c ? ' ring-2 ring-offset-1 ring-slate-400' : ' border border-slate-300')} />
                );
              })}
              <div className="flex-1" />
              <button onClick={function cancel() { setAdding(false); setDraft(''); }}
                data-testid="fav-add-cancel"
                className="text-xs text-slate-500 px-2 py-1">Abbrechen</button>
              <button onClick={addItem}
                data-testid="fav-add-submit"
                className="text-xs px-3 py-1 rounded-lg bg-blue-800 text-white hover:bg-blue-900">Hinzufügen</button>
            </div>
          </div>
        )}
      </div>

      {!adding && !editing && (
        <div className="pt-2 mt-2 border-t border-slate-100">
          <button onClick={function startAdd() { setAdding(true); }}
            data-testid="fav-add-btn"
            className="w-full text-sm px-3 py-2 rounded-xl border border-dashed border-slate-300 text-slate-600 hover:bg-slate-50 flex items-center justify-center gap-1.5">
            <Plus className="h-4 w-4" /> Favorit hinzufügen
          </button>
        </div>
      )}
    </div>
  );
}

function loadAssistantSession() {
  try { return JSON.parse(window.localStorage.getItem('tn_assistant_session') || '{}'); }
  catch (e) { return {}; }
}

export default function AssistantWidget() {
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [tab, setTab] = useState('chat');
  const [expanded, setExpanded] = useState(false);
  const [conversationId, setConversationId] = useState(function initConv() {
    return loadAssistantSession().conversationId || null;
  });
  const [messages, setMessages] = useState(function initMsgs() {
    var s = loadAssistantSession();
    return Array.isArray(s.messages) ? s.messages : [];
  });
  const [pendingPrompt, setPendingPrompt] = useState(null);
  const navigate = useNavigate();
  const { isAdminLike } = useAuth();

  useEffect(function persistAssistantSession() {
    try {
      window.localStorage.setItem('tn_assistant_session',
        JSON.stringify({ conversationId: conversationId, messages: messages }));
    } catch (e) {}
  }, [conversationId, messages]);

  useEffect(function toggleAssistantBodyClass() {
    if (open) { document.body.classList.add('tn-assistant-open'); }
    else { document.body.classList.remove('tn-assistant-open'); }
    return function cleanup() { document.body.classList.remove('tn-assistant-open'); };
  }, [open]);

  useEffect(function suppressPwManagerIcons() {
    if (!open) { return undefined; }
    var SELS = ['[data-lastpass-icon-root]', 'div[data-lastpass-infield="true"]',
                'div[data-lastpass-root]', '[data-dashlane-rid]', 'com-1password-button'];
    function hideAll() {
      SELS.forEach(function eachSel(sel) {
        document.querySelectorAll(sel).forEach(function eachNode(n) {
          n.style.setProperty('display', 'none', 'important');
          n.style.setProperty('visibility', 'hidden', 'important');
        });
      });
    }
    hideAll();
    var obs = new MutationObserver(hideAll);
    obs.observe(document.body, { childList: true, subtree: true });
    var iv = window.setInterval(hideAll, 1000);
    return function cleanup() { obs.disconnect(); window.clearInterval(iv); };
  }, [open]);

  const handleOpenConversation = useCallback(function openConv(convId) {
    assistantAPI.conversation(convId).then(function onLoad(r) {
      var conv = r.data;
      var msgs = [];
      var turns = conv.turns || [];
      turns.forEach(function mapTurn(t) {
        msgs.push({ role: 'user', text: t.q });
        msgs.push({ role: 'assistant', text: t.a, cards: t.cards || [],
                    sources: t.sources || [],
                    turnId: t.id || null, conversationId: conv.id, rating: t.rating || null });
      });
      setMessages(msgs);
      setConversationId(conv.id);
      setTab('chat');
    }).catch(function onErr() {});
  }, []);

  const handleNewChat = useCallback(function newChat() {
    setConversationId(null);
    setMessages([]);
    setTab('chat');
  }, []);

  function openPanel() { setClosing(false); setOpen(true); }
  function closePanel() {
    setClosing(true);
    window.setTimeout(function finishClose() { setOpen(false); setClosing(false); }, 180);
  }

  if (!isAdminLike()) return null;

  return (
    <>
      {!open && (
        <button
          data-testid="assistant-launcher"
          onClick={openPanel}
          className="tn-launcher-in group fixed bottom-6 right-6 z-50 h-16 w-16 rounded-full shadow-lg bg-white hover:shadow-2xl hover:scale-110 flex items-center justify-center transition-all"
        >
          <img src={kiIcon} alt="Assistent öffnen"
            className="h-10 w-10 object-contain transition-transform group-hover:scale-110" />
        </button>
      )}

      {open && (
        <div
          data-testid="assistant-panel"
          className={"fixed bottom-6 right-6 z-50 bg-white rounded-2xl shadow-2xl border border-slate-200 " +
                     "flex flex-col overflow-hidden max-w-[calc(100vw-2rem)] " +
                     (closing ? "tn-panel-exit " : "tn-panel-enter ") +
                     (expanded ? "w-[620px] h-[820px] max-h-[90vh]" : "w-[440px] h-[680px] max-h-[85vh]")}
        >
          <div className="flex items-center gap-1 px-2 py-2 border-b border-slate-100">
            <div className="flex gap-1 flex-1 min-w-0">
              <TabButton active={tab === 'chat'} onClick={function setChat() { setTab('chat'); }}
                icon={<MessageCircle className="h-4 w-4" />} label="Chat" testId="tab-chat" />
              <TabButton active={tab === 'favorites'} onClick={function setFav() { setTab('favorites'); }}
                icon={<Star className="h-4 w-4" />} label="Favoriten" testId="tab-favorites" />
              <TabButton active={tab === 'history'} onClick={function setHist() { setTab('history'); }}
                icon={<History className="h-4 w-4" />} label="Verlauf" testId="tab-history" />
            </div>
            <button onClick={handleNewChat} aria-label="Neuer Chat" title="Neuer Chat"
              data-testid="assistant-newchat-btn"
              className="shrink-0 p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100">
              <RotateCcw className="h-5 w-5" />
            </button>
            <button onClick={function toggleSize() { setExpanded(function p(v) { return !v; }); }}
              aria-label={expanded ? "Verkleinern" : "Vergrößern"} data-testid="assistant-resize-btn"
              className="shrink-0 p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100">
              {expanded ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
            </button>
            <button onClick={closePanel} aria-label="Schließen" data-testid="assistant-close-btn"
              className="shrink-0 p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-hidden p-3">
            {tab === 'chat' ? (
              <ChatTab navigate={navigate} messages={messages} setMessages={setMessages}
                conversationId={conversationId} setConversationId={setConversationId}
                pendingPrompt={pendingPrompt}
                onPendingConsumed={function consume() { setPendingPrompt(null); }} />
            ) : tab === 'favorites' ? (
              <FavoritesTab onUse={function usePrompt(text) { setPendingPrompt(text); setTab('chat'); }} />
            ) : (
              <HistoryView onOpen={handleOpenConversation} onNew={handleNewChat} />
            )}
          </div>
        </div>
      )}
    </>
  );
}

function TabButton({ active, onClick, icon, label, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`flex-1 flex items-center justify-center gap-1.5 text-[13px] py-2 rounded-lg transition
                  ${active ? 'bg-slate-100 text-slate-900 font-medium' : 'text-slate-500 hover:bg-slate-50'}`}>
      {icon}{label}
    </button>
  );
}

function HistoryView({ onOpen, onNew }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(function load() {
    assistantAPI.conversations().then(function onOk(r) {
      setItems(r.data.conversations || []);
    }).catch(function onErr() {}).finally(function done() { setLoading(false); });
  }, []);
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-medium text-slate-700">Verlauf</p>
        <button onClick={onNew} data-testid="history-new-chat"
          className="flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900">
          <Plus className="h-3.5 w-3.5" /> Neuer Chat
        </button>
      </div>
      {loading && <div className="flex items-center gap-2 text-sm text-slate-500 py-4"><Loader2 className="h-4 w-4 animate-spin" /> Laden ...</div>}
      <div className="flex-1 overflow-y-auto space-y-1.5">
        {items.map(function renderItem(c) {
          return <HistoryItem key={c.id} item={c} onClick={function go() { onOpen(c.id); }} />;
        })}
      </div>
      {!loading && items.length === 0 && <p className="text-sm text-slate-400 px-1 py-4 text-center">Noch kein Verlauf.</p>}
    </div>
  );
}

var WAVE_BARS = [0, 120, 240, 80, 200, 40, 160, 300, 100, 220, 60, 180, 280, 140, 20, 260, 90, 210];

function ChatTab({ navigate, messages, setMessages, conversationId, setConversationId, pendingPrompt, onPendingConsumed }) {
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [quota, setQuota] = useState(null);
  const [pwLock, setPwLock] = useState(true);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(function loadQuota() {
    assistantAPI.limitStatus().then(function onOk(r) { setQuota(r.data); }).catch(function onErr() {});
  }, []);

  const didInitialScroll = useRef(false);
  useEffect(function scrollToEnd() {
    var mode = didInitialScroll.current ? 'smooth' : 'auto';
    endRef.current?.scrollIntoView({ behavior: mode, block: 'end' });
    didInitialScroll.current = true;
  }, [messages, busy]);

  const send = useCallback(async function doSend(text) {
    var q = (text ?? input).trim();
    if (!q || busy) return;
    setMessages(function addUser(m) { return [...m, { role: 'user', text: q }]; });
    setInput(''); setBusy(true);
    try {
      var res = await assistantAPI.chat(q, conversationId);
      setMessages(function addAssistant(m) {
        return [...m, { role: 'assistant', text: res.data.answer, cards: res.data.cards || [],
                        sources: res.data.sources || [],
                        turnId: res.data.turn_id || null,
                        conversationId: res.data.conversation_id || conversationId || null }];
      });
      if (res.data.conversation_id && !conversationId) {
        setConversationId(res.data.conversation_id);
      }
      if (res.data.quota) setQuota(res.data.quota);
    } catch (e) {
      var detail = fehlertext(e, 'Da ging etwas schief.');
      setMessages(function addError(m) { return [...m, { role: 'assistant', text: detail, cards: [] }]; });
    } finally { setBusy(false); }
  }, [input, busy, conversationId, setMessages, setConversationId]);

  useEffect(function runPending() {
    if (pendingPrompt) {
      send(pendingPrompt);
      onPendingConsumed();
    }
  }, [pendingPrompt]); // eslint-disable-line react-hooks/exhaustive-deps

  var blocked = quota && !quota.unlimited && quota.limit != null && quota.remaining <= 0;

  function stopTracks() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(function stopTrack(t) { t.stop(); });
      streamRef.current = null;
    }
  }

  async function startRecording() {
    if (recording || blocked) return;
    try {
      var stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      var mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = function onData(e) {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch (err) {
      stopTracks();
      setRecording(false);
    }
  }

  function cancelRecording() {
    var mr = mediaRecorderRef.current;
    if (mr && mr.state !== 'inactive') { mr.onstop = null; mr.stop(); }
    chunksRef.current = [];
    stopTracks();
    setRecording(false);
  }

  async function transcribeBlob(blob) {
    setTranscribing(true);
    try {
      var text = await assistantAPI.transcribe(blob);
      if (text) {
        setInput(function appendText(prev) { return (prev ? prev + ' ' : '') + text; });
      }
    } catch (err) {
      // stilles Scheitern
    } finally {
      setTranscribing(false);
      if (inputRef.current) inputRef.current.focus();
    }
  }

  function confirmRecording() {
    var mr = mediaRecorderRef.current;
    if (!mr) { setRecording(false); return; }
    mr.onstop = function onStop() {
      var blob = new Blob(chunksRef.current, { type: 'audio/webm' });
      chunksRef.current = [];
      stopTracks();
      setRecording(false);
      if (blob.size > 0) transcribeBlob(blob);
    };
    if (mr.state !== 'inactive') mr.stop();
  }

  var suggestions = [
    'Wer hat diesen Monat nicht abgegeben?',
    'Aktenzeichen für …',
    'Stunden-Übersicht für …',
  ];

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto space-y-3 pr-1">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center px-2 -translate-y-3">
            <div className="tn-logo-wrap">
              <div className="tn-logo-glow" />
              <div className="tn-logo-ring">
                <img src={kiIcon} alt="" className="tn-logo-img" />
              </div>
            </div>
            <p className="text-sm text-slate-600 mt-5 mb-4 font-medium">Frag mich alles über deine Daten.</p>
            <div className="space-y-2 w-full max-w-[300px]">
              {suggestions.map(function renderSuggestion(s) {
                return (
                  <button key={s} onClick={function onClickSugg() {
                      var t = s.trim();
                      if (t.endsWith('\u2026') || t.endsWith('...')) {
                        var base = t.replace('\u2026', '').replace(/\.{3}$/, '').trim();
                        setInput(base + ' ');
                        if (inputRef.current) { inputRef.current.focus(); }
                      } else {
                        send(s);
                      }
                    }}
                    className="block w-full text-sm px-3 py-2 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition">
                    {s}
                  </button>
                );
              })}
            </div>
            <p className="mt-5 text-[11px] leading-relaxed text-slate-400">
              Die Antworten fassen echte Daten aus TimeNYzer zusammen. Bei wichtigen Dingen schau am besten
              kurz selbst in den Stundenzettel — so bist du immer auf der sicheren Seite.
            </p>
          </div>
        )}
        {messages.map(function renderMsg(m, i) {
          if (m.role === 'user') return <UserBubble key={i} text={m.text} />;
          return <AssistantBubble key={i} text={m.text} cards={m.cards} sources={m.sources} navigate={navigate}
                   conversationId={m.conversationId} turnId={m.turnId} initialRating={m.rating} />;
        })}
        {busy && (
          <div className="flex">
            <div className="inline-flex items-center gap-1.5 rounded-2xl bg-slate-100 px-3 py-2.5">
              <span className="tn-typing-dot" />
              <span className="tn-typing-dot" />
              <span className="tn-typing-dot" />
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="pt-2 mt-2 border-t border-slate-100">
        <div className="flex items-center gap-2">
          {recording ? (
            <div className="flex-1 flex items-center gap-2 px-3 py-1.5 rounded-xl border border-red-200 bg-red-50/50">
              <span className="tn-rec-dot" />
              <div className="tn-wave flex-1">
                {WAVE_BARS.map(function renderBar(d, i) {
                  return <span key={i} style={{ animationDelay: d + 'ms' }} />;
                })}
              </div>
              <button type="button" onClick={cancelRecording} aria-label="Abbrechen"
                className="h-8 w-8 rounded-full text-slate-500 hover:bg-slate-200 flex items-center justify-center">
                <X className="h-4 w-4" />
              </button>
              <button type="button" onClick={confirmRecording} aria-label="Fertig"
                className="h-8 w-8 rounded-full bg-blue-800 text-white hover:bg-blue-900 flex items-center justify-center">
                <Check className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <>
              <input
                ref={inputRef}
                type="text"
                name="tn-assistant-frage"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                data-1p-ignore="true"
                data-lpignore="true"
                data-bwignore="true"
                data-form-type="other"
                readOnly={pwLock}
                onFocus={function unlockInput() { setPwLock(false); }}
                value={input}
                onChange={function onInputChange(e) { setInput(e.target.value); }}
                onKeyDown={function onKey(e) { if (e.key === 'Enter') send(); }}
                placeholder={blocked ? 'Tageslimit erreicht' : 'Frag mich etwas ...'}
                disabled={blocked}
                data-testid="assistant-chat-input"
                className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:opacity-50"
              />
              <button type="button" onClick={startRecording} disabled={blocked || transcribing}
                aria-label="Aufnehmen" data-testid="assistant-chat-mic"
                className="h-9 w-9 rounded-xl text-slate-500 hover:text-slate-800 hover:bg-slate-100 flex items-center justify-center disabled:opacity-50">
                {transcribing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic className="h-4 w-4" />}
              </button>
              <button onClick={function onSend() { send(); }} disabled={busy || blocked}
                data-testid="assistant-chat-send"
                className="h-9 w-9 rounded-xl bg-blue-800 text-white flex items-center justify-center hover:bg-blue-900 disabled:opacity-50">
                <Send className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
        {quota && blocked && (
          <p className="text-[11px] text-slate-400 mt-1">
            Tageslimit erreicht — morgen wieder.
          </p>
        )}
      </div>
    </div>
  );
}
